import { inject, Injectable } from '@angular/core';
import { FilterBy } from '@shared/sync/filter-by.model';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { Appointment } from '../models/appointment.model';
import { AppointmentMapper } from '../mappers/appointment.mapper';
import { DatePipe } from '@angular/common';
import { Task } from '../models/task.model';
import { GetTaskInPaginationResponse } from '../models/get-task-in-pagination-response.model';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class AppointmentService {
  private readonly _serviceKey = 'kfmAppointment';
  private readonly _blazorService = inject(HsBlazorService);
  private readonly _mapper = new AppointmentMapper();
  private readonly _datePipe = new DatePipe('de-DE');

  public async getAll(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
    showClosed = false,
    showAccomplished = false,
  ): Promise<IPaginatedItemsModel<Task>> {
    const appointmentResult = await this._blazorService.invokeService(
      this._serviceKey,
      'Get',
      {
        pageIndex: pageIndex,
        pageSize: pageSize,
        showClosed: showClosed,
        showAccomplished: showAccomplished,
        sortBy: sortBy,
        filterBy: filterBy,
      },
    );

    if (appointmentResult) {
      const result = appointmentResult as Record<string, unknown>;
      const appointments: IPaginatedItemsModel<Task> = {
        count: Number(result['count']),
        data: (result['data'] as Task[]).map((a: Task) =>
          this._mapper.taskAppointmentMap(a),
        ),
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
      };

      return appointments;
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async getInPagination(
    appointmentId: string,
    from: Date,
    to: Date,
    pageSize = 10,
    showClosed = false,
    showAccomplished = false,
    sortBy: SortBy | null = null,
  ): Promise<GetTaskInPaginationResponse | undefined> {
    const appointmentResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetInPagination',
      {
        appointmentId: appointmentId,
        pageSize: pageSize,
        from: from,
        to: to,
        showClosed: showClosed,
        showAccomplished: showAccomplished,
        sortBy: sortBy,
      },
    );

    if (appointmentResult) {
      const result = appointmentResult as Record<string, unknown>;
      const appointments: GetTaskInPaginationResponse = {
        count: Number(result['count']),
        data: (result['data'] as Task[]).map((a: Task) =>
          this._mapper.taskAppointmentMap(a),
        ),
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
        from: new Date(String(result['from'])),
        to: new Date(String(result['to'])),
      };

      return appointments;
    }

    return undefined;
  }

  public async getById(id: string): Promise<Task | undefined> {
    if (!id || id.trim().length === 0) return undefined;

    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetByIds',
      { ids: [id] },
    );

    let task: Task | undefined;

    if (result) {
      const appointments = result as Record<string, unknown>;
      const data = appointments['data'] as Task[];

      if (data && data.length > 0)
        return this._mapper.taskAppointmentMap(data[0]);
    }

    return task;
  }

  public async getOfMonth(
    date: Date,
    showClosed = false,
    showAccomplished = false,
  ): Promise<Task[]> {
    const dateFrom = new Date(
      date.getFullYear(),
      date.getMonth(),
      1,
      0,
      0,
      0,
      0,
    );
    const dateTo = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const sortModel: SortBy = {
      sort: 0, // 0 = ASC, 1 = DESC
      property: 'startDate',
    };

    const filterModel: FilterBy = {
      filterStatement: `return i.startDate >= p[0] && i.endDate <= p[1]`,
      parameters: [
        this._datePipe.transform(dateFrom, 'yyyy-MM-ddT00:00:00'),
        this._datePipe.transform(dateTo, 'yyyy-MM-ddT23:59:59'),
      ],
    };

    const result = await this.getAll(
      -1,
      -1,
      sortModel,
      filterModel,
      showClosed,
      showAccomplished,
    );

    return result.data;
  }

  public async create(
    taskId?: string,
    customerId?: string,
    locationId?: string,
    date?: Date,
  ): Promise<Appointment | undefined> {
    const request = {
      taskId: taskId,
      customerId: customerId,
      locationId: locationId,
      date: date ? this._mapper.toLocalISOString(date) : undefined,
    };
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Create',
      request,
    );

    if (result) {
      const appointment = (result as Record<string, unknown>)[
        'appointment'
      ] as Appointment;
      return this._mapper.appointmentMap(appointment);
    }

    return undefined;
  }

  public async update(
    value: Appointment,
    taskId: string | undefined,
    customerId: string | undefined,
    locationId: string | undefined,
  ): Promise<boolean> {
    const request = this._mapper.mapAppointmentRequest(value);
    request.TaskId = taskId;
    request.CustomerId = customerId;
    request.LocationId = locationId;

    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Update',
      request,
    );

    return !!result;
  }

  public async delete(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Delete',
      { Id: id },
    );

    const deleted = (result as Record<string, unknown>)['deleted'];
    return !!deleted;
  }

  public async convert(appointmentId: string): Promise<string | undefined> {
    const request = { appointmentId: appointmentId };
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Convert',
      request,
    );

    const taskId = (result as Record<string, unknown>)['taskId'] as
      | string
      | undefined;
    return taskId;
  }
}
